import { useState, useRef, useEffect } from 'react';  
import { Send, Search, MoreVertical, User, Phone, VideoIcon, Pencil, Smile } from 'lucide-react';  
import Header from '../components/layout/Header';  
import { Button } from '../components/ui/button';  
import { Input } from '../components/ui/input';  
import { ScrollArea } from '../components/ui/scroll-area';  
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';  
import { useToast } from '../hooks/use-toast';  
import { useIsMobile } from '../hooks/use-mobile';  
import { Textarea } from '../components/ui/textarea';  

// Define message and chat types to provide better type safety  
type Message = {  
  id: string;  
  text: string;  
  sender: 'me' | 'other'; // Changed to a union type  
  time: string;  
  status: 'read' | 'sent';  
};  

type Chat = {  
  id: string;  
  name: string;  
  avatar: string;  
  lastMessage: string;  
  time: string;  
  unread: boolean;  
  status: 'online' | 'offline'; // Added status type  
  conversation: Message[];  
};  

// Sample chat data - fixed the quote escape issue
const sampleChats:any[] = [
  {
    id: '1',
    name: 'Jane Cooper',
    avatar: 'https://i.pravatar.cc/150?img=1',
    lastMessage: "Yeah sure, tell me zafar",
    time: 'just now',
    unread: false,
    status: 'online',
    conversation: [
      {
        id: '1',
        text: "Hello and thanks for signing up to the course. If you have any questions about the course or Adobe XD, feel free to get in touch and I'll be happy to help.",
        sender: 'other',
        time: '9:30',
        status: 'read'
      },
      {
        id: '2',
        text: 'Hello, Good Evening',
        sender: 'me',
        time: '9:35',
        status: 'read'
      },
      {
        id: '3',
        text: "I'm Zafar",
        sender: 'me',
        time: '9:35',
        status: 'read'
      },
      {
        id: '4',
        text: 'I only have a small doubt about your lecture, can you give me some time for this?',
        sender: 'me',
        time: '9:36',
        status: 'read'
      },
      {
        id: '5',
        text: 'Yeah sure, tell me zafar',
        sender: 'other',
        time: '9:38',
        status: 'read'
      },
      {
        id: '6', 
        text: "Hello, Good Evening",
        sender: 'me',
        time: '9:55',
        status: 'read'
      },
      {
        id: '7',
        text: "I'm Zafar",
        sender: 'me',
        time: '9:55',
        status: 'read'
      },
      {
        id: '8',
        text: "I only have a small doubt about your lecture, can you give me some time for this?",
        sender: 'me',
        time: '9:56',
        status: 'read'
      },
      {
        id: '9',
        text: "Hello and thanks for signing up to the course. If you have any questions about the course or Adobe XD, feel free to get in touch and I'll be happy to help.",
        sender: 'other',
        time: '10:30',
        status: 'read'
      },
    ]
  },
  {
    id: '2',
    name: 'Jerry Wilson',
    avatar: 'https://i.pravatar.cc/150?img=2',
    lastMessage: "Thank you so much, sir",
    time: '2 d',
    unread: true,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'Hi Jerry, how are you doing with the assignment?',
        sender: 'me',
        time: '14:22',
        status: 'read'
      },
      {
        id: '2',
        text: 'I finished it yesterday. Just had a few questions about the feedback.',
        sender: 'other',
        time: '14:25',
        status: 'read'
      },
      {
        id: '3',
        text: 'Sure, what questions do you have?',
        sender: 'me',
        time: '14:30',
        status: 'read'
      },
      {
        id: '4',
        text: 'Thank you so much, sir',
        sender: 'other',
        time: '14:35',
        status: 'read'
      },
    ]
  },
  {
    id: '3',
    name: 'Marvin McKinney',
    avatar: 'https://i.pravatar.cc/150?img=3',
    lastMessage: "You're Welcome",
    time: '1 m',
    unread: false,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'Hello, how can I help you?',
        sender: 'me',
        time: '10:22',
        status: 'read'
      },
      {
        id: '2',
        text: "You're Welcome",
        sender: 'other',
        time: '10:25',
        status: 'read'
      }
    ]
  },
  {
    id: '4',
    name: 'Eleanor Pena',
    avatar: 'https://i.pravatar.cc/150?img=4',
    lastMessage: 'Thank you so much, sir',
    time: '1 m',
    unread: false,
    status: 'online',
    conversation: [
      {
        id: '1',
        text: 'Thank you so much, sir',
        sender: 'other',
        time: '11:25',
        status: 'read'
      }
    ]
  },
  {
    id: '5',
    name: 'Ronald Richards',
    avatar: 'https://i.pravatar.cc/150?img=7',
    lastMessage: "Sorry, I can't help you.",
    time: '2 m',
    unread: false,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'Can you help me with this?',
        sender: 'me',
        time: '13:25',
        status: 'read'
      },
      {
        id: '2',
        text: "Sorry, I can't help you.",
        sender: 'other',
        time: '13:30',
        status: 'read'
      }
    ]
  },
  {
    id: '6',
    name: 'Kathryn Murphy',
    avatar: 'https://i.pravatar.cc/150?img=6',
    lastMessage: 'new message',
    time: '2 m',
    unread: true,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'new message',
        sender: 'other',
        time: '15:25',
        status: 'read'
      }
    ]
  },
  {
    id: '7',
    name: 'Jacob Jones',
    avatar: 'https://i.pravatar.cc/150?img=8',
    lastMessage: 'Thank you so much, sir',
    time: '6 m',
    unread: false,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'Thank you so much, sir',
        sender: 'other',
        time: '16:25',
        status: 'read'
      }
    ]
  },
  {
    id: '8',
    name: 'Cameron Williamson',
    avatar: 'https://i.pravatar.cc/150?img=9',
    lastMessage: "It's okay, no problem brother, I will fix everything",
    time: '6 m',
    unread: false,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: "It's okay, no problem brother, I will fix everything",
        sender: 'other',
        time: '17:25',
        status: 'read'
      }
    ]
  },
  {
    id: '9',
    name: 'Kristin Watson',
    avatar: 'https://i.pravatar.cc/150?img=10',
    lastMessage: 'new message',
    time: '1 y',
    unread: true,
    status: 'online',
    conversation: [
      {
        id: '1',
        text: 'new message',
        sender: 'other',
        time: '18:25',
        status: 'read'
      }
    ]
  },
  {
    id: '10',
    name: 'Kirsten Watson',
    avatar: 'https://i.pravatar.cc/150?img=10',
    lastMessage: 'new message',
    time: '1 y',
    unread: true,
    status: 'offline',
    conversation: [
      {
        id: '1',
        text: 'new message',
        sender: 'other',
        time: '19:25',
        status: 'read'
      }
    ]
  },
];
  

const Chat = () => {  
  const { toast } = useToast();  
  const isMobile = useIsMobile();  
  const [selectedChat, setSelectedChat] = useState<Chat | null>(sampleChats[0]);  
  const [message, setMessage] = useState('');  
  const [chats, setChats] = useState<Chat[]>(sampleChats);  
  const [searchTerm, setSearchTerm] = useState('');  
  const [showChatList, setShowChatList] = useState(!isMobile);  
  const [showComposeForm, setShowComposeForm] = useState(false);  
  const [newMessageData, setNewMessageData] = useState({  
    recipient: '',  
    subject: '',  
    message: ''  
  });  
  const messagesEndRef = useRef<HTMLDivElement | null>(null);  
  
  useEffect(() => {  
    if (isMobile) {  
      setShowChatList(selectedChat === null);  
    } else {  
      setShowChatList(true);  
    }  
  }, [isMobile, selectedChat]);  

  useEffect(() => {  
    scrollToBottom();  
  }, [selectedChat]);  

  const scrollToBottom = () => {  
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });  
  };  

  const filteredChats = chats.filter(chat =>   
    chat.name.toLowerCase().includes(searchTerm.toLowerCase())  
  );  

  const handleSendMessage = (e: React.FormEvent<HTMLFormElement>) => {  
    e.preventDefault();  
    if (!message.trim()) return;  

    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });  
    
    const newMessage: Message = {  
      id: Date.now().toString(),  
      text: message,  
      sender: 'me',  
      time: currentTime,  
      status: 'sent'  
    };  
    
    const updatedChats = chats.map(chat => {  
      if (chat.id === selectedChat?.id) {  
        return {  
          ...chat,  
          conversation: [...chat.conversation, newMessage],  
          lastMessage: message,  
          time: 'just now'  
        };  
      }  
      return chat;  
    });  

    setChats(updatedChats);  
    setSelectedChat({  
      ...selectedChat!,  
      conversation: [...selectedChat!.conversation, newMessage],  
      lastMessage: message,  
      time: 'just now'  
    });  
    setMessage('');  
    
    // Simulate receiving a reply  
    if (selectedChat?.id === '1') {  
      setTimeout(() => {  
        const replyMessage: Message = {  
          id: Date.now().toString(),  
          text: "Yes, that's right. I'll be in touch shortly with more details.",  
          sender: 'other',  
          time: currentTime,  
          status: 'read'  
        };  
        
        const updatedChatsWithReply = chats.map(chat => {  
          if (chat.id === selectedChat?.id) {  
            return {  
              ...chat,  
              conversation: [...chat.conversation, newMessage, replyMessage],  
              lastMessage: replyMessage.text,  
              time: 'just now'  
            };  
          }  
          return chat;  
        });  
        
        setChats(updatedChatsWithReply);  
        setSelectedChat({  
          ...selectedChat!,  
          conversation: [...selectedChat!.conversation, newMessage, replyMessage],  
          lastMessage: replyMessage.text,  
          time: 'just now'  
        });  
      }, 2000);  
    }  
  };  

  const handleChatSelect = (chat: Chat) => {  
    setSelectedChat(chat);  
    if (isMobile) {  
      setShowChatList(false);  
    }  
  };  

  const handleBackToList = () => {  
    if (isMobile) {  
      setShowChatList(true);  
    }  
  };  

  const formatMessageTime = (time: string) => {  
    return time;  
  };  

  const handleComposeClick = () => {  
    setShowComposeForm(true);  
  };  

  const handleComposeClose = () => {  
    setShowComposeForm(false);  
    setNewMessageData({  
      recipient: '',  
      subject: '',  
      message: ''  
    });  
  };  

  const handleComposeSubmit = (e: React.FormEvent<HTMLFormElement>) => {  
    e.preventDefault();  
    
    // Create a new chat with the composed message  
    const newChatId = (chats.length + 1).toString();  
    const currentTime = new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });  
    
    const newChat: Chat = {  
      id: newChatId,  
      name: newMessageData.recipient,  
      avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 10) + 1}`,  
      lastMessage: newMessageData.message,  
      time: 'just now',  
      unread: false,  
      status: 'offline',  
      conversation: [  
        {  
          id: '1',  
          text: newMessageData.message,  
          sender: 'me',  
          time: currentTime,  
          status: 'sent'  
        }  
      ]  
    };  
    
    setChats([newChat, ...chats]);  
    setSelectedChat(newChat);  
    setShowComposeForm(false);  
    setNewMessageData({  
      recipient: '',  
      subject: '',  
      message: ''  
    });  
    
    toast({  
      title: "Message sent",  
      description: "Your message has been sent successfully",  
    });  
  };  

  const groupMessagesByDate = (messages: Message[]) => {  
    const groups: Record<string, Message[]> = {};  
    
    messages.forEach(message => {  
      const dateGroup = 'Today'; // Simplified; in a real app, would use actual date logic  
      if (!groups[dateGroup]) {  
        groups[dateGroup] = [];  
      }  
      groups[dateGroup].push(message);  
    });  
    
    return groups;  
  };  

  return (  
    <div className="h-full flex flex-col bg-gray-50">  
      <Header title="Messages" subtitle="Chat with students and instructors" />  
      
      {showComposeForm && (  
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">  
          <div className="bg-white rounded-lg w-full max-w-md p-6">  
            <div className="flex justify-between items-center mb-4">  
              <h2 className="text-lg font-semibold">Compose Message</h2>  
              <Button   
                variant="ghost"   
                size="sm"   
                className="text-gray-500"  
                onClick={handleComposeClose}  
              >  
                ✕  
              </Button>  
            </div>  
            
            <form onSubmit={handleComposeSubmit} className="space-y-4">  
              <div>  
                <label className="text-sm font-medium block mb-1">To:</label>  
                <Input   
                  placeholder="Recipient name"   
                  value={newMessageData.recipient}  
                  onChange={(e) => setNewMessageData({...newMessageData, recipient: e.target.value})}  
                  required  
                />  
              </div>  
              
              <div>  
                <label className="text-sm font-medium block mb-1">Subject:</label>  
                <Input   
                  placeholder="Subject"   
                  value={newMessageData.subject}  
                  onChange={(e) => setNewMessageData({...newMessageData, subject: e.target.value})}  
                  required  
                />  
              </div>  
              
              <div>  
                <label className="text-sm font-medium block mb-1">Message:</label>  
                <Textarea   
                  placeholder="Type your message"   
                  className="min-h-[120px]"  
                  value={newMessageData.message}  
                  onChange={(e) => setNewMessageData({...newMessageData, message: e.target.value})}  
                  required  
                />  
              </div>  
              
              <div className="flex justify-end pt-2">  
                <Button   
                  type="button"   
                  variant="outline"   
                  className="mr-2"  
                  onClick={handleComposeClose}  
                >  
                  Cancel  
                </Button>  
                <Button   
                  type="submit"   
                  className="bg-bytesplash-blue hover:bg-bytesplash-blue/90"  
                >  
                  <Send size={18} className="mr-2" />  
                  Send  
                </Button>  
              </div>  
            </form>  
          </div>  
        </div>  
      )}  
      
      <div className="flex-1 flex overflow-hidden">  
        {/* Chat List */}  
        {showChatList && (  
          <div className="w-full md:w-80 lg:w-96 border-r border-gray-200 bg-white flex flex-col">  
            <div className="p-4 border-b">  
              <div className="flex justify-between items-center mb-4">  
                <h2 className="text-lg font-semibold">Chat</h2>  
                <Button   
                  variant="outline"   
                  size="sm"  
                  className="bg-bytesplash-soft-blue text-bytesplash-blue border-none hover:bg-bytesplash-soft-blue/80"  
                  onClick={handleComposeClick}  
                >  
                  <Pencil size={16} className="mr-1" />  
                  Compose  
                </Button>  
              </div>  
              <div className="relative">  
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={16} />  
                <Input   
                  placeholder="Search"   
                  className="pl-9 border-gray-200"  
                  value={searchTerm}  
                  onChange={(e) => setSearchTerm(e.target.value)}  
                />  
              </div>  
            </div>  
            
            <ScrollArea className="flex-1">  
              <div className="divide-y">  
                {filteredChats.map(chat => (  
                  <div   
                    key={chat.id}   
                    className={`p-3 hover:bg-gray-50 cursor-pointer ${selectedChat?.id === chat.id ? 'bg-bytesplash-soft-blue' : ''}`}  
                    onClick={() => handleChatSelect(chat)}  
                  >  
                    <div className="flex items-start gap-3">  
                      <div className="relative">  
                        <Avatar>  
                          <AvatarImage src={chat.avatar} />  
                          <AvatarFallback>{chat?.name?.charAt(0)}</AvatarFallback>  
                        </Avatar>  
                        {chat.status === 'online' && (  
                          <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>  
                        )}  
                      </div>  
                      <div className="flex-1 min-w-0">  
                        <div className="flex justify-between items-start">  
                          <p className="font-medium truncate">{chat.name}</p>  
                          <span className="text-xs text-gray-500 whitespace-nowrap ml-2">{chat.time}</span>  
                        </div>  
                        <p className={`text-sm truncate ${chat.unread ? 'font-medium text-bytesplash-blue' : 'text-gray-600'}`}>  
                          {chat.lastMessage}  
                        </p>  
                      </div>  
                      {chat.unread && (  
                        <div className="w-2 h-2 rounded-full bg-bytesplash-blue mt-1.5"></div>  
                      )}  
                    </div>  
                  </div>  
                ))}  
              </div>  
            </ScrollArea>  
          </div>  
        )}  
        
        {/* Chat Area */}  
        {selectedChat && !showChatList && (  
          <div className="flex-1 flex flex-col">  
            {/* Chat Header */}  
            <div className="p-4 border-b bg-white flex justify-between items-center">  
              <div className="flex items-center gap-3">  
                {isMobile && (  
                  <Button   
                    variant="ghost"   
                    size="sm"   
                    className="mr-1 -ml-2"  
                    onClick={handleBackToList}  
                  >  
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">  
                      <path d="M15 19L8 12L15 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>  
                    </svg>  
                  </Button>  
                )}  
                <div className="relative">  
                  <Avatar>  
                    <AvatarImage src={selectedChat.avatar} />  
                    <AvatarFallback>{selectedChat.name.charAt(0)}</AvatarFallback>  
                  </Avatar>  
                  {selectedChat.status === 'online' && (  
                    <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full"></span>  
                  )}  
                </div>  
                <div>  
                  <h3 className="font-medium">{selectedChat.name}</h3>  
                  <p className="text-xs text-gray-500">  
                    {selectedChat.status === 'online' ? (  
                      <span className="text-green-500">Active now</span>  
                    ) : (  
                      'Offline'  
                    )}  
                  </p>  
                </div>  
              </div>  
              <div className="flex gap-2">  
                <Button variant="ghost" size="icon" className="text-gray-500 hover:bg-gray-100 rounded-full">  
                  <Phone size={20} />  
                </Button>  
                <Button variant="ghost" size="icon" className="text-gray-500 hover:bg-gray-100 rounded-full">  
                  <VideoIcon size={20} />  
                </Button>  
                <Button variant="ghost" size="icon" className="text-gray-500 hover:bg-gray-100 rounded-full">  
                  <MoreVertical size={20} />  
                </Button>  
              </div>  
            </div>  
            
            {/* Messages */}  
            <ScrollArea className="flex-1 p-4 bg-gray-50">  
              <div className="space-y-4 mb-4">  
                {selectedChat.conversation.length > 0 ? (  
                  <>  
                    {Object.entries(groupMessagesByDate(selectedChat.conversation)).map(([date, messages]) => (  
                      <div key={date} className="space-y-4">  
                        <div className="text-center">  
                          <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">{date}</span>  
                        </div>  

                        {messages.map(msg => (  
                          <div   
                            key={msg.id}   
                            className={`flex ${msg.sender === 'me' ? 'justify-end' : 'justify-start'}`}  
                          >  
                            {/* Render Avatar only for incoming messages */}  
                            {msg.sender !== 'me' && (  
                              <div className="mr-2 flex-shrink-0">  
                                <Avatar className="h-8 w-8" aria-label={`${selectedChat.name}'s avatar`}>  
                                  <AvatarImage src={selectedChat.avatar} alt={`${selectedChat.name}'s avatar`} />  
                                  <AvatarFallback>{selectedChat.name.charAt(0)}</AvatarFallback>  
                                </Avatar>  
                              </div>  
                            )}  

                            <div className={`max-w-[75%] ${msg.sender === 'me' ? 'bg-bytesplash-blue text-white' : 'bg-white'} rounded-lg px-4 py-2 shadow-sm`}>  
                              <p>{msg.text}</p>  
                              <div className={`text-xs mt-1 ${msg.sender === 'me' ? 'text-blue-100' : 'text-gray-500'} text-right`}>  
                                {formatMessageTime(msg.time)}  
                              </div>  
                            </div>  

                            {/* Render user's Avatar for outgoing messages */}  
                            {msg.sender === 'me' && (  
                              <div className="ml-2 flex-shrink-0">  
                                <Avatar className="h-8 w-8" aria-label="My avatar">  
                                  <AvatarImage src="https://i.pravatar.cc/150?img=12" alt="My avatar" />  
                                  <AvatarFallback>Me</AvatarFallback>  
                                </Avatar>  
                              </div>  
                            )}  
                          </div>  
                        ))}  
                      </div>  
                    ))}  
                  </>  
                ) : (  
                  <div className="h-full flex items-center justify-center text-gray-500">  
                    <div className="text-center">  
                      <User size={40} className="mx-auto text-gray-300 mb-2" />  
                      <p>No messages yet</p>  
                      <p className="text-sm">Start a conversation with {selectedChat.name}</p>  
                    </div>  
                  </div>  
                )}  
                <div ref={messagesEndRef} />  
              </div>  
            </ScrollArea>  
            
            {/* Message Input */}  
            <div className="p-4 border-t bg-white">  
              <form onSubmit={handleSendMessage} className="flex gap-2">  
                <Button   
                  type="button"   
                  variant="ghost"   
                  size="icon"   
                  className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"  
                >  
                  <Smile size={20} />  
                </Button>  
                <div className="flex-1 relative">  
                  <Textarea   
                    placeholder="Type your message"   
                    className="resize-none pr-12"  
                    value={message}  
                    onChange={(e) => setMessage(e.target.value)}  
                  />  
                  <Button   
                    type="submit"   
                    disabled={!message.trim()}  
                    className="absolute right-2 bottom-2 bg-bytesplash-blue hover:bg-bytesplash-blue/90 h-8 px-3"  
                  >  
                    <Send size={16} />  
                  </Button>  
                </div>  
              </form>  
            </div>  
          </div>  
        )}  
        
        {/* Empty State */}  
        {!selectedChat && !showChatList && (  
          <div className="flex-1 flex flex-col items-center justify-center bg-gray-50 text-gray-500">  
            <User size={48} className="mb-4 text-gray-300" />  
            <p className="text-lg font-medium mb-2">No chat selected</p>  
            <p className="text-sm">Select a conversation from the list to start chatting</p>  
            {isMobile && (  
              <Button   
                className="mt-4 bg-bytesplash-blue hover:bg-bytesplash-blue/90"   
                onClick={() => setShowChatList(true)}  
              >  
                View Conversations  
              </Button>  
            )}  
          </div>  
        )}  
      </div>  
    </div>  
  );  
};  

export default Chat;  